import React, { useMemo } from 'react';
import { Button, Tag, Dropdown, Tooltip } from 'antd';
import {
  PlusOutlined,
  StarFilled,
  EyeInvisibleOutlined,
  MoreOutlined,
} from '@ant-design/icons';

import { DevPlanAdmin } from 'state';

type Props = {
  plans: DevPlanAdmin[];
  activeCode?: string;
  onSelect: (code: string) => void;
  onCreate: () => void;
  onClone: (plan: DevPlanAdmin) => void;
  onHide: (plan: DevPlanAdmin) => void;
  onUnhide: (plan: DevPlanAdmin) => void;
  onDelete: (plan: DevPlanAdmin) => void;
};

/** Left rail — every tree grouped by `family` (its version lineage), newest last. */
const PlanRail: React.FC<Props> = ({
  plans,
  activeCode,
  onSelect,
  onCreate,
  onClone,
  onHide,
  onUnhide,
  onDelete,
}) => {
  const families = useMemo(() => {
    const byFamily: Record<string, DevPlanAdmin[]> = {};
    plans.forEach((p) => {
      const fam = p.family || p.code;
      (byFamily[fam] = byFamily[fam] || []).push(p);
    });
    return Object.entries(byFamily)
      .map(([fam, list]) => ({
        fam,
        list: list.slice().sort((a, b) => a.version - b.version),
      }))
      .sort((a, b) => a.fam.localeCompare(b.fam));
  }, [plans]);

  return (
    <div className="devadmin-rail">
      <div className="devadmin-rail__head">
        <span>Trees</span>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onCreate}>
          New
        </Button>
      </div>

      <div className="devadmin-rail__list">
        {families.map(({ fam, list }) => (
          <div key={fam} className="devadmin-family">
            <div className="devadmin-family__name">{fam}</div>
            {list.map((plan) => {
              const menuItems = [
                { key: 'clone', label: 'Clone to new draft' },
                plan.hidden
                  ? { key: 'unhide', label: 'Unhide' }
                  : { key: 'hide', label: 'Hide from trainees' },
                {
                  key: 'delete',
                  label: 'Delete draft',
                  danger: true,
                  disabled: plan.status !== 'draft',
                },
              ];
              const onMenu = ({ key }: { key: string }) => {
                if (key === 'clone') onClone(plan);
                else if (key === 'hide') onHide(plan);
                else if (key === 'unhide') onUnhide(plan);
                else if (key === 'delete') onDelete(plan);
              };
              return (
                <div
                  key={plan.code}
                  className={`devadmin-planrow${
                    plan.code === activeCode ? ' is-active' : ''
                  }`}
                  onClick={() => onSelect(plan.code)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && onSelect(plan.code)}
                >
                  <div className="devadmin-planrow__main">
                    <span className="devadmin-planrow__name">{plan.name}</span>
                    <span className="devadmin-planrow__meta">
                      <Tag
                        color={plan.status === 'draft' ? 'gold' : 'green'}
                        bordered={false}
                      >
                        {plan.status}
                      </Tag>
                      <span className="devadmin-muted">v{plan.version}</span>
                      {plan.is_default && (
                        <Tooltip title="Default tree">
                          <StarFilled className="devadmin-planrow__default" />
                        </Tooltip>
                      )}
                      {plan.hidden && (
                        <Tooltip title="Hidden from trainees">
                          <EyeInvisibleOutlined />
                        </Tooltip>
                      )}
                    </span>
                  </div>
                  <Dropdown
                    trigger={['click']}
                    menu={{ items: menuItems, onClick: onMenu }}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>
              );
            })}
          </div>
        ))}
        {plans.length === 0 && (
          <div className="devadmin-muted" style={{ padding: 16 }}>
            No trees yet. Create one to start.
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanRail;
